import { useCallback, useEffect, useMemo, useState } from 'react';
import { SAKTI_DEFAULTS } from '../apiConfig';
import SaktiApiService from '../services/saktiApiService';
import SaktiBccApiService from '../services/saktiBccApiService';
import { getTodayInJakartaForApi } from '../utils/date';
import {
  SaktiFilterOptions,
  SaktiPaginationMeta,
  SaktiBccKpiData,
  SaktiBccListItem,
  SaktiBaListItem,
  SaktiFilters,
} from '../types';

export type SaktiBccTabKey = 'all' | 'restan' | 'ba' | 'ba_restan' | 'ba_list';

const EMPTY_OPTIONS: SaktiFilterOptions = {
  companies: [],
  estates: [],
  divisions: [],
};

const EMPTY_KPI: SaktiBccKpiData = {
  total_bcc: 0,
  total_bunch: 0,
  total_unripe: 0,
  total_overripe: 0,
  total_loose_fruit: 0,
  total_restan: 0,
};

const EMPTY_PAGINATION: SaktiPaginationMeta = {
  page: 1,
  limit: SAKTI_DEFAULTS.DEFAULT_LIMIT,
  total_items: 0,
  total_pages: 0,
};

interface UseSaktiBccDashboardResult {
  options: SaktiFilterOptions;
  loadingOptions: boolean;
  loadingKpi: boolean;
  loadingTable: boolean;
  activeTab: SaktiBccTabKey;
  draftFilters: SaktiFilters;
  appliedFilters: SaktiFilters;
  kpi: SaktiBccKpiData;
  rows: (SaktiBccListItem | SaktiBaListItem)[];
  pagination: SaktiPaginationMeta;
  tableError: string | null;
  onDraftFilterChange: (nextFilters: SaktiFilters) => void;
  applyFilters: () => Promise<void>;
  setActiveTab: (tab: SaktiBccTabKey) => void;
  goToPage: (page: number) => Promise<void>;
  reloadAll: () => Promise<void>;
}

export const useSaktiBccDashboard = (): UseSaktiBccDashboardResult => {
  const initialFilters: SaktiFilters = useMemo(() => ({
    date: getTodayInJakartaForApi(),
    company_id: SAKTI_DEFAULTS.DEFAULT_COMPANY_ID,
  }), []);

  const [options, setOptions] = useState<SaktiFilterOptions>(EMPTY_OPTIONS);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(true);
  const [loadingKpi, setLoadingKpi] = useState<boolean>(true);
  const [loadingTable, setLoadingTable] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<SaktiBccTabKey>('all');
  const [draftFilters, setDraftFilters] = useState<SaktiFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<SaktiFilters>(initialFilters);
  const [kpi, setKpi] = useState<SaktiBccKpiData>(EMPTY_KPI);
  const [rows, setRows] = useState<(SaktiBccListItem | SaktiBaListItem)[]>([]);
  const [pagination, setPagination] = useState<SaktiPaginationMeta>(EMPTY_PAGINATION);
  const [tableError, setTableError] = useState<string | null>(null);

  const fetchTableData = useCallback(async (tab: SaktiBccTabKey, filters: SaktiFilters, page: number = 1) => {
    setLoadingTable(true);
    setTableError(null);
    try {
      let response;
      const params = { ...filters, page, limit: SAKTI_DEFAULTS.DEFAULT_LIMIT };
      
      switch (tab) {
        case 'restan':
          response = await SaktiBccApiService.getRestan(params);
          break;
        case 'ba':
          // Back-end filters the list? The API doc says getList can have sub-filters, 
          // but for simplicity we assume there are specific endpoints or we use params.
          // Based on API doc 3.2, 3.4, 3.6:
          response = await SaktiBccApiService.getList(params); // Should have a filter for BA?
          break;
        case 'ba_restan':
          response = await SaktiBccApiService.getBaRestan(params);
          break;
        case 'ba_list':
          response = await SaktiBccApiService.getBaList(params);
          break;
        case 'all':
        default:
          response = await SaktiBccApiService.getList(params);
          break;
      }

      if (response.success) {
        setRows(response.data);
        setPagination(response.pagination);
      }
    } catch (error) {
      setTableError(error instanceof Error ? error.message : 'Gagal memuat data tabel BCC');
    } finally {
      setLoadingTable(false);
    }
  }, []);

  const fetchKpi = useCallback(async (filters: SaktiFilters) => {
    setLoadingKpi(true);
    try {
      const response = await SaktiBccApiService.getKpi(filters);
      if (response.success) {
        setKpi(response.data);
      }
    } catch (error) {
      // toast error
    } finally {
      setLoadingKpi(false);
    }
  }, []);

  const loadFilterOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const fetchedOptions = await SaktiApiService.fetchFilterOptions();
      setOptions(fetchedOptions);
      
      const defaultCompany = fetchedOptions.companies.find(c => c.id === SAKTI_DEFAULTS.DEFAULT_COMPANY_ID)?.id 
        || fetchedOptions.companies[0]?.id 
        || SAKTI_DEFAULTS.DEFAULT_COMPANY_ID;

      const nextFilters = { ...initialFilters, company_id: defaultCompany };
      setDraftFilters(nextFilters);
      setAppliedFilters(nextFilters);
      
      fetchKpi(nextFilters);
      fetchTableData('all', nextFilters);
    } finally {
      setLoadingOptions(false);
    }
  }, [initialFilters, fetchKpi, fetchTableData]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  const onDraftFilterChange = useCallback((nextFilters: SaktiFilters) => {
    setDraftFilters(nextFilters);
  }, []);

  const applyFilters = useCallback(async () => {
    setAppliedFilters(draftFilters);
    fetchKpi(draftFilters);
    fetchTableData(activeTab, draftFilters, 1);
  }, [draftFilters, activeTab, fetchKpi, fetchTableData]);

  const handleTabChange = useCallback((tab: SaktiBccTabKey) => {
    setActiveTab(tab);
    fetchTableData(tab, appliedFilters, 1);
  }, [appliedFilters, fetchTableData]);

  const goToPage = useCallback(async (page: number) => {
    fetchTableData(activeTab, appliedFilters, page);
  }, [activeTab, appliedFilters, fetchTableData]);

  const reloadAll = useCallback(async () => {
    fetchKpi(appliedFilters);
    fetchTableData(activeTab, appliedFilters, pagination.page);
  }, [appliedFilters, activeTab, pagination.page, fetchKpi, fetchTableData]);

  return {
    options,
    loadingOptions,
    loadingKpi,
    loadingTable,
    activeTab,
    draftFilters,
    appliedFilters,
    kpi,
    rows,
    pagination,
    tableError,
    onDraftFilterChange,
    applyFilters,
    setActiveTab: handleTabChange,
    goToPage,
    reloadAll,
  };
};

export default useSaktiBccDashboard;
